import { Badge, Button, Dropdown, Input, Menu, Modal, Popover, Select, Space, Table, Tooltip } from "antd";
import { EditOutlined, EyeOutlined, FilterOutlined } from "@ant-design/icons";
import "bootstrap-icons/font/bootstrap-icons.css";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import GetTotalSuppliers from "../../api/getTotalSuppliers";
import { getSupplierSummary } from "../account/account.api";
import { loadSuppliers, loadAllSuppliersForSearch } from "../../redux/actions/supplier/getSuppliersAction";
import { getSupplierBalanceParts } from "../../utils/partyBalanceLabels";
import { getPartyListBalance } from "../../utils/partyListBalance";
import AddSup from "./addSup";
import "./suppliers.css";

const FILTER_STYLE = { marginBottom: 12 };
const FILTER_LABEL = { display: "block", marginBottom: 4, color: "rgba(0,0,0,0.65)", fontSize: 12 };

const SupplierBalanceDisplay = ({ value }) => {
  const parts = getSupplierBalanceParts(value);
  if (!parts) return <span>—</span>;
  return (
    <div className="party-balance-cell-inner">
      <span className="party-balance-amt">{parts.primary}</span>
      <div className="party-balance-label">{parts.label}</div>
    </div>
  );
};

function CustomTable({
  list,
  total,
  status,
  columnsToShow,
  setColumnsToShow,
  colVisibilityClickHandler,
  columnItems,
  isSearching,
  filteredList,
  addSupplierButton,
  uploadButton,
  onEditSupplier,
  resolvePartyBalance,
}) {
  const dispatch = useDispatch();
  const addKeys = (arr) => arr.map((i) => ({ ...i, key: i.id }));
  const hasCol = (key) => Array.isArray(columnsToShow) && columnsToShow.some((c) => c.key === key);
  const listToRender = isSearching ? filteredList : list;

  return (
    <div className="list-page-table-container">
      <div className="list-page-table-inner">
        <div className="list-page-table-wrap">
          {/* Desktop Table */}
          <div className="d-none d-lg-block">
            <Table
              className="party-list-table"
              scroll={{ x: 1200, y: 450 }}
              loading={!list}
              pagination={{
                defaultPageSize: 10,
                pageSizeOptions: [10, 20, 50, 100, 200],
                showSizeChanger: true,
                total: isSearching ? filteredList.length : total,
                onChange: (page, limit) => {
                  if (!isSearching) {
                    dispatch(loadSuppliers({ page, limit, status }));
                  }
                },
              }}
              columns={columnsToShow}
              dataSource={
                isSearching ? addKeys(filteredList) : list ? addKeys(list) : []
              }
            />
          </div>

          {/* Mobile cards */}
          <div className="d-lg-none sup-mobile-cards">
            {listToRender && listToRender.length > 0 ? (
              <div className="sup-mobile-list">
                {listToRender.map((record, idx) => (
                  <div key={record.id ?? idx} className="sup-mobile-card">
                    <div className="sup-mobile-card-body">
                      {hasCol("name") ? (
                        <div className="sup-mobile-name">
                          <Link to={`/supplier/${record.id}`}>{record.name || "—"}</Link>
                        </div>
                      ) : (
                        <div className="sup-mobile-name">{record.name || "—"}</div>
                      )}

                      {hasCol("address") && (
                        <div className="sup-mobile-line">
                          <span className="sup-mobile-label">Address</span>
                          <span className="sup-mobile-value">
                            {record.address || "—"}
                          </span>
                        </div>
                      )}

                      {hasCol("phone") && (
                        <div className="sup-mobile-line">
                          <span className="sup-mobile-label">Phone</span>
                          <span className="sup-mobile-value">{record.phone || "—"}</span>
                        </div>
                      )}

                      {hasCol("balance") && (
                        <div className="sup-mobile-line sup-mobile-line-balance">
                          <span className="sup-mobile-label">Balance</span>
                          <span className="sup-mobile-value">
                            <SupplierBalanceDisplay
                              value={
                                resolvePartyBalance
                                  ? resolvePartyBalance(record)
                                  : record.balance ??
                                    Number(record.opening_balance ?? 0) + Number(record.invoice_due_total ?? 0)
                              }
                            />
                          </span>
                        </div>
                      )}

                      <div className="sup-mobile-actions party-list-icon-actions">
                        <Tooltip title="View details">
                          <Link to={`/supplier/${record.id}`}>
                            <Button type="text" size="small" icon={<EyeOutlined style={{ fontSize: 18 }} />} />
                          </Link>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined style={{ fontSize: 18 }} />}
                            onClick={() => onEditSupplier && onEditSupplier(record)}
                          />
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-5">
                <p className="text-muted mb-0">No suppliers found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const GetAllSup = ({ refreshTrigger, addSupplierButton, uploadButton }) => {
  const dispatch = useDispatch();
  const list = useSelector((state) => state.suppliers.list);
  const searchList = useSelector((state) => state.suppliers.searchList);
  const summaryDueBySupplierIdRef = useRef(new Map());
  const [, setSummaryMergeTick] = useState(0);
  const [total, setTotal] = useState(0);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filters, setFilters] = useState({ name: "", phone: "", address: "" });
  const [filteredList, setFilteredList] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [columnItems, setColumnItems] = useState([]);
  const [columnsToShow, setColumnsToShow] = useState([]);
  const [status, setStatus] = useState("true");

  const getListLimit = () =>
    typeof window !== "undefined" && window.innerWidth < 992 ? 1000 : 10;

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSupplierData, setEditSupplierData] = useState(null);

  const handleEditSupplier = (supplier) => {
    setEditSupplierData(supplier);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditSupplierData(null);
  };

  useEffect(() => {
    dispatch(loadSuppliers({ status, page: 1, limit: getListLimit() }));
  }, [dispatch, status, refreshTrigger]);

  useEffect(() => {
    GetTotalSuppliers().then((res) => setTotal(res));
  }, [list]);

  useEffect(() => {
    dispatch(loadAllSuppliersForSearch({ status }));
  }, [status, dispatch]);

  useEffect(() => {
    let cancelled = false;
    getSupplierSummary()
      .then((res) => {
        if (cancelled) return;
        const m = new Map();
        (res?.list || []).forEach((row) => {
          const id = Number(row.id);
          if (Number.isFinite(id)) m.set(id, Number(row.total_due) || 0);
        });
        summaryDueBySupplierIdRef.current = m;
        setSummaryMergeTick((t) => t + 1);
      })
      .catch(() => {
        if (cancelled) return;
        summaryDueBySupplierIdRef.current = new Map();
        setSummaryMergeTick((t) => t + 1);
      });
    return () => {
      cancelled = true;
    };
  }, [status]);

  useEffect(() => {
    const name = (filters.name || "").trim().toLowerCase();
    const phone = (filters.phone || "").trim().toLowerCase();
    const address = (filters.address || "").trim().toLowerCase();
    if (!searchList?.length || (!name && !phone && !address)) {
      setIsSearching(false);
      setFilteredList([]);
      return;
    }
    setIsSearching(true);
    const filtered = searchList.filter((supplier) => {
      if (name && !(supplier.name || "").toLowerCase().includes(name)) return false;
      if (phone && !(supplier.phone || "").toLowerCase().includes(phone)) return false;
      if (address && !(supplier.address || "").toLowerCase().includes(address)) return false;
      return true;
    });
    setFilteredList(filtered);
  }, [searchList, filters.name, filters.phone, filters.address]);
  
  // Column visibility functionality
  const colVisibilityClickHandler = (col) => {
    const ifColFound = columnsToShow.find((item) => item.key === col.key);
    if (ifColFound) {
      const filteredColumnsToShow = columnsToShow.filter(
        (item) => item.key !== col.key
      );
      setColumnsToShow(filteredColumnsToShow);
    } else {
      const foundIndex = columns.findIndex((item) => item.key === col.key);
      const foundCol = columns.find((item) => item.key === col.key);
      let updatedColumnsToShow = [...columnsToShow];
      updatedColumnsToShow.splice(foundIndex, 0, foundCol);
      setColumnsToShow(updatedColumnsToShow);
    }
  };
  
  const columns = [
    {
      title: "S.No",
      key: "sNo",
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      ellipsis: true,
      className: "party-list-col-name",
      render: (name, { id }) => <Link to={`/supplier/${id}`}>{name}</Link>,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      responsive: ["md"],
      width: 240,
      ellipsis: false,
      className: "party-list-col-address",
      render: (text) => (
        <span className="party-list-address-text">{text && String(text).trim() ? text : "—"}</span>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 132,
      className: "party-list-col-phone",
    },
    {
      title: (
        <Tooltip
          title={
            "Positive = Payable (you owe the supplier). " +
            "Negative = Advance / Prepaid (supplier credit or overpayment). " +
            "Includes opening balance + purchase invoice dues for this company."
          }
        >
          <span>Balance</span>
        </Tooltip>
      ),
      dataIndex: "balance",
      key: "balance",
      width: 160,
      align: "right",
      className: "party-list-col-balance",
      sorter: (a, b) =>
        getPartyListBalance(a, summaryDueBySupplierIdRef.current) -
        getPartyListBalance(b, summaryDueBySupplierIdRef.current),
      render: (_, record) => (
        <SupplierBalanceDisplay
          value={getPartyListBalance(record, summaryDueBySupplierIdRef.current)}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      fixed: "right",
      className: "party-list-col-action party-list-icon-actions",
      render: (_, record) => (
        <Space size={4} wrap={false}>
          <Tooltip title="View details">
            <Link to={`/supplier/${record.id}`}>
              <Button type="text" size="small" icon={<EyeOutlined style={{ fontSize: 18 }} />} />
            </Link>
          </Tooltip>
          <Tooltip title="Edit">
            <Link to={`/supplier/${record.id}/update`} state={{ data: record }}>
              <Button type="text" size="small" icon={<EditOutlined style={{ fontSize: 18 }} />} />
            </Link>
          </Tooltip>
        </Space>
      ),
    },
  ];
  
  const menuItems = columns.map((item) => {
    return {
      key: item.key,
      label: <span>{item.title}</span>,
    };
  });
  
  useEffect(() => {
    setColumnItems(menuItems);
    setColumnsToShow(columns);
  }, []);
  
  const activeFilterCount = [filters.name, filters.phone, filters.address].filter(Boolean).length;

  const filterContent = (
    <div style={{ width: 280 }}>
      <div style={FILTER_STYLE}>
        <label style={FILTER_LABEL}>Name</label>
        <Input
          placeholder="Filter by name"
          value={filters.name}
          onChange={(e) => setFilters((f) => ({ ...f, name: e.target.value }))}
          allowClear
        />
      </div>
      <div style={FILTER_STYLE}>
        <label style={FILTER_LABEL}>Phone</label>
        <Input
          placeholder="Filter by phone"
          value={filters.phone}
          onChange={(e) => setFilters((f) => ({ ...f, phone: e.target.value }))}
          allowClear
        />
      </div>
      <div style={FILTER_STYLE}>
        <label style={FILTER_LABEL}>Address</label>
        <Input
          placeholder="Filter by address"
          value={filters.address}
          onChange={(e) => setFilters((f) => ({ ...f, address: e.target.value }))}
          allowClear
        />
      </div>
      <div style={FILTER_STYLE}>
        <label style={FILTER_LABEL}>Status</label>
        <Select
          placeholder="Status"
          style={{ width: "100%" }}
          value={status === "true" ? "true" : "false"}
          onChange={(value) => {
            setStatus(value);
            dispatch(loadSuppliers({ status: value, page: 1, limit: getListLimit() }));
          }}
        >
          <Select.Option value="true">Active</Select.Option>
          <Select.Option value="false">Inactive</Select.Option>
        </Select>
      </div>
      <div style={{ textAlign: "center", marginTop: 8 }}>
        <Button
          size="small"
          onClick={() => {
            setFilters({ name: "", phone: "", address: "" });
          }}
        >
          Clear All Filters
        </Button>
      </div>
    </div>
  );

  return (
    <div className="card column-design list-page-fill-viewport">
      <div className="card-body">
        <div className="card-title d-flex justify-content-between align-items-center list-page-header" style={{ marginBottom: 0 }}>
          <h5 className="mb-0">Supplier List</h5>
          <div className="sup-header-actions">
            {list && (
              <>
                <Popover
                  content={filterContent}
                  title="Filters"
                  trigger="click"
                  open={filterVisible}
                  onOpenChange={setFilterVisible}
                  placement="bottomLeft"
                >
                  <Badge count={activeFilterCount} size="small" offset={[-2, 2]}>
                    <Button
                      type="primary"
                      className="filter-icon-btn"
                      icon={<FilterOutlined />}
                      onClick={() => setFilterVisible((v) => !v)}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    />
                  </Badge>
                </Popover>
                <Dropdown
                  overlay={<Menu onClick={colVisibilityClickHandler} items={columnItems} />}
                  placement="bottomRight"
                >
                  <Button className="column-visibility">Column Visibility</Button>
                </Dropdown>
              </>
            )}
            {addSupplierButton}
            {uploadButton}
          </div>
        </div>
        <CustomTable
          list={list}
          total={total}
          status={status}
          columnsToShow={columnsToShow}
          setColumnsToShow={setColumnsToShow}
          colVisibilityClickHandler={colVisibilityClickHandler}
          columnItems={columnItems}
          isSearching={isSearching}
          filteredList={filteredList}
          addSupplierButton={addSupplierButton}
          uploadButton={uploadButton}
          onEditSupplier={handleEditSupplier}
          resolvePartyBalance={(record) =>
            getPartyListBalance(record, summaryDueBySupplierIdRef.current)
          }
        />
      </div>

      <Modal
        title="Edit Supplier"
        open={editModalOpen}
        footer={null}
        destroyOnClose
        onCancel={closeEditModal}
      >
        {editSupplierData ? (
          <AddSup
            inModal
            mode="edit"
            supplierId={editSupplierData.id}
            prefillData={editSupplierData}
            onCancel={closeEditModal}
            onSuccess={() => {
              closeEditModal();
              dispatch(loadSuppliers({ status, page: 1, limit: getListLimit() }));
            }}
          />
        ) : null}
      </Modal>
    </div>
  );
};

export default GetAllSup;